import { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { laufwerkStatus, type LaufwerkStatus } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import VerbindungKarte from '../components/laufwerk/VerbindungKarte';
import IndexKarte from '../components/laufwerk/IndexKarte';
import AenderungenKarte from '../components/laufwerk/AenderungenKarte';
import AusschluesseKarte from '../components/laufwerk/AusschluesseKarte';
import RechtegruppenKarte from '../components/laufwerk/RechtegruppenKarte';
import QuellenKarte from '../components/laufwerk/QuellenKarte';

const STATUS_INTERVALL_MS = 15_000;

/**
 * Laufwerk (M2, nur Administratoren): Quellen (E32), Verbindung zum Connector, Index-Stand, Ausschlüsse und
 * Rechtegruppen. Der Status (Verbindung + Index) wird alle 15 Sekunden neu geladen. Nach der Anmeldung bei
 * Google/Microsoft/Dropbox kommt der Browser mit `?quelle=…&verbunden=1` bzw. `&oauth_fehler=…` hierher zurück.
 */
export default function Laufwerk() {
  const auth = useAuth();
  const [sp, setSp] = useSearchParams();
  const [status, setStatus] = useState<LaufwerkStatus | null>(null);
  const [statusFehler, setStatusFehler] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; variant: 'ok' | 'err' } | null>(null);

  const flash = useCallback((msg: string, variant: 'ok' | 'err' = 'ok') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const ladeStatus = useCallback(async () => {
    try { setStatus(await laufwerkStatus()); setStatusFehler(null); }
    catch (e: any) { setStatusFehler(e?.message ?? String(e)); }
  }, []);

  // Rückkehr von der Anmeldung beim Anbieter: Meldung zeigen, Adresse aufräumen
  useEffect(() => {
    if (!sp.get('quelle')) return;
    if (sp.get('verbunden')) flash('Verbunden — die Dateien werden jetzt gelesen.');
    else if (sp.get('oauth_fehler')) flash(`Anmeldung nicht abgeschlossen: ${sp.get('oauth_fehler')}`, 'err');
    setSp({}, { replace: true });
    setTimeout(() => document.getElementById('quellen')?.scrollIntoView({ block: 'start' }), 300);
  }, [sp, setSp, flash]);

  useEffect(() => {
    if (!auth.isAdmin) return;
    void ladeStatus();
    const t = setInterval(() => { if (!document.hidden) void ladeStatus(); }, STATUS_INTERVALL_MS);
    return () => clearInterval(t);
  }, [auth.isAdmin, ladeStatus]);

  // Nur Administratoren — die Navigation zeigt „Laufwerk" ohnehin nur ihnen, das hier fängt die direkte Adresse ab.
  if (!auth.isLoading && !auth.isAdmin) return <Navigate to="/" replace />;
  if (auth.isLoading) return null;

  return (
    <div className="space-y-4">
      <div className="card">
        <h1 className="page-h1">Laufwerk</h1>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-ps-dark-400">
          Quellen, Verbindung, Index und Zugriffsrechte für das Laufwerk Ihrer Firma. Die Dateien werden nur gelesen, nie verändert.
        </p>
      </div>

      {statusFehler && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Der Status konnte nicht geladen werden: {statusFehler}
        </div>
      )}

      <QuellenKarte flash={flash} />
      <VerbindungKarte status={status} flash={flash} onChanged={() => void ladeStatus()} />
      <IndexKarte status={status} />
      <AenderungenKarte />
      <AusschluesseKarte flash={flash} />
      <RechtegruppenKarte flash={flash} />

      {toast && (
        <div className={`fixed bottom-6 right-6 z-40 flex max-w-[calc(100vw-3rem)] items-center gap-2 rounded-md border px-4 py-3 text-[13px] shadow-md ${toast.variant === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
          {toast.variant === 'ok' ? <CheckIcon className="h-3.5 w-3.5 shrink-0" /> : <XMarkIcon className="h-3.5 w-3.5 shrink-0" />}
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
